import axiosClient from '../../../common/axiosClient';
import { SaveReason } from '../shared/types/monitoring-report.types';
import { ReportChangedError } from '../shared/utils/reportChangedError';

/**
 * Version-history plumbing for magic report writes.
 *
 * - Stale-save check: every overwriting write sends the report version this tab last saw
 *   (X-Report-Version); the backend refuses it with 409 "report_changed" if the report has changed
 *   since. Each response carries the new version, so the calls inside one save chain correctly.
 * - Save context: while a save runs, its requests carry the wizard step and the reason for the
 *   save (X-Save-Step / X-Save-Reason), which decide how the backend groups revisions.
 */

const VERSION_HEADER = 'X-Report-Version';
const STEP_HEADER = 'X-Save-Step';
const REASON_HEADER = 'X-Save-Reason';

const versions = new Map<string, string>();
const activeSaves = new Map<string, { reason: SaveReason; step?: string }>();
const activeSteps = new Map<string, string>();

/** The main wizard step the user is on; used for saves that don't name one (Save Draft, autosave). */
export const setActiveStep = (reportId: string, step: string | undefined) => {
  if (step) activeSteps.set(reportId, step);
  else activeSteps.delete(reportId);
};

export const beginSave = (reportId: string, options: { reason: SaveReason; step?: string }) => {
  activeSaves.set(reportId, options);
};

export const endSave = (reportId: string) => {
  activeSaves.delete(reportId);
};

const buildHeaders = (reportId: string) => {
  const headers: Record<string, string> = {};
  const version = versions.get(reportId);
  if (version) headers[VERSION_HEADER] = version;

  const save = activeSaves.get(reportId);
  const step = save?.step ?? activeSteps.get(reportId);
  if (step) headers[STEP_HEADER] = step;
  if (save?.reason) headers[REASON_HEADER] = save.reason;
  return headers;
};

const rememberVersion = (reportId: string, headers: any) => {
  const version = headers?.[VERSION_HEADER.toLowerCase()];
  if (version) versions.set(reportId, String(version));
};

/** An undo was refused because later versions changed some of the same keys. */
export class RevertConflictError extends Error {
  constructor(public readonly conflicts: { key: string; revisionNumbers: number[] }[]) {
    super('Later versions changed some of the same sections.');
    this.name = 'RevertConflictError';
  }
}

const toError = (error: any): Error => {
  const data = error?.response?.data;
  if (error?.response?.status === 409 && data?.code === 'report_changed') return new ReportChangedError();
  if (error?.response?.status === 409 && data?.code === 'revert_conflict') return new RevertConflictError(data.conflicts ?? []);
  if (typeof data === 'string') return new Error(data);
  if (typeof data?.title === 'string') return new Error(data.title);
  if (typeof data?.message === 'string') return new Error(data.message);
  return error;
};

/**
 * Sends a request for one magic report with its version and save-context headers, and remembers
 * the version the response carries. A 409 never updates the remembered version: the tab is stale
 * and must reload rather than silently adopt the newer version.
 *
 * Pass `remember: false` for reads that don't load the report into the editor (e.g. a refresh of
 * its invoices): adopting their version without their content would hide a stale tab.
 */
export const versionedRequest = async <T>(
  reportId: string,
  method: 'get' | 'put' | 'post' | 'delete',
  url: string,
  data?: unknown,
  { remember = true }: { remember?: boolean } = {},
): Promise<T> => {
  try {
    const response = await axiosClient.request({ method, url, data, headers: buildHeaders(reportId) });
    if (remember) rememberVersion(reportId, response.headers);
    return response.data as T;
  } catch (error: any) {
    if (remember && error?.response?.status !== 409) rememberVersion(reportId, error?.response?.headers);
    throw toError(error);
  }
};
